import {
  parsePhoneNumber,
  getCountryCallingCode,
  getCountries,
  isValidPhoneNumber,
  type CountryCode,
  type PhoneNumber,
} from 'libphonenumber-js'
import currencyCodes from 'currency-codes-ts'

export type { CountryCode, PhoneNumber }

export interface CountryInfo {
  code: CountryCode
  name: string
  dialCode: string
  flag: string
  currency: string
  currencySymbol: string
}

export interface PhoneValidationResult {
  isValid: boolean
  country?: CountryCode
  nationalNumber?: string
  internationalNumber?: string
  type?: 'mobile' | 'fixed-line' | 'toll-free' | 'premium-rate' | 'unknown'
}

const FLAG_EMOJIS: Partial<Record<CountryCode, string>> = {
  AF: '🇦🇫', AL: '🇦🇱', DZ: '🇩🇿', AS: '🇦🇸', AD: '🇦🇩', AO: '🇦🇴', AI: '🇦🇮', AG: '🇦🇬',
  AR: '🇦🇷', AM: '🇦🇲', AW: '🇦🇼', AU: '🇦🇺', AT: '🇦🇹', AZ: '🇦🇿', BS: '🇧🇸', BH: '🇧🇭',
  BD: '🇧🇩', BB: '🇧🇧', BY: '🇧🇾', BE: '🇧🇪', BZ: '🇧🇿', BJ: '🇧🇯', BM: '🇧🇲', BT: '🇧🇹',
  BO: '🇧🇴', BA: '🇧🇦', BW: '🇧🇼', BR: '🇧🇷', BN: '🇧🇳', BG: '🇧🇬', BF: '🇧🇫', BI: '🇧🇮',
  CV: '🇨🇻', KH: '🇰🇭', CM: '🇨🇲', CA: '🇨🇦', KY: '🇰🇾', CF: '🇨🇫', TD: '🇹🇩', CL: '🇨🇱',
  CN: '🇨🇳', CO: '🇨🇴', KM: '🇰🇲', CG: '🇨🇬', CD: '🇨🇩', CK: '🇨🇰', CR: '🇨🇷', CI: '🇨🇮',
  HR: '🇭🇷', CU: '🇨🇺', CY: '🇨🇾', CZ: '🇨🇿', DK: '🇩🇰', DJ: '🇩🇯', DM: '🇩🇲', DO: '🇩🇴',
  EC: '🇪🇨', EG: '🇪🇬', SV: '🇸🇻', GQ: '🇬🇶', ER: '🇪🇷', EE: '🇪🇪', SZ: '🇸🇿', ET: '🇪🇹',
  FJ: '🇫🇯', FI: '🇫🇮', FR: '🇫🇷', GA: '🇬🇦', GM: '🇬🇲', GE: '🇬🇪', DE: '🇩🇪', GH: '🇬🇭',
  GR: '🇬🇷', GD: '🇬🇩', GT: '🇬🇹', GN: '🇬🇳', GW: '🇬🇼', GY: '🇬🇾', HT: '🇭🇹', HN: '🇭🇳',
  HU: '🇭🇺', IS: '🇮🇸', IN: '🇮🇳', ID: '🇮🇩', IR: '🇮🇷', IQ: '🇮🇶', IE: '🇮🇪', IL: '🇮🇱',
  IT: '🇮🇹', JM: '🇯🇲', JP: '🇯🇵', JO: '🇯🇴', KZ: '🇰🇿', KE: '🇰🇪', KI: '🇰🇮', KW: '🇰🇼',
  KG: '🇰🇬', LA: '🇱🇦', LV: '🇱🇻', LB: '🇱🇧', LS: '🇱🇸', LR: '🇱🇷', LY: '🇱🇾', LI: '🇱🇮',
  LT: '🇱🇹', LU: '🇱🇺', MG: '🇲🇬', MW: '🇲🇼', MY: '🇲🇾', MV: '🇲🇻', ML: '🇲🇱', MT: '🇲🇹',
  MH: '🇲🇭', MR: '🇲🇷', MU: '🇲🇺', MX: '🇲🇽', FM: '🇫🇲', MD: '🇲🇩', MC: '🇲🇨', MN: '🇲🇳',
  ME: '🇲🇪', MA: '🇲🇦', MZ: '🇲🇿', MM: '🇲🇲', NA: '🇳🇦', NR: '🇳🇷', NP: '🇳🇵', NL: '🇳🇱',
  NZ: '🇳🇿', NI: '🇳🇮', NE: '🇳🇪', NG: '🇳🇬', NO: '🇳🇴', OM: '🇴🇲', PK: '🇵🇰', PW: '🇵🇼',
  PS: '🇵🇸', PA: '🇵🇦', PG: '🇵🇬', PY: '🇵🇾', PE: '🇵🇪', PH: '🇵🇭', PL: '🇵🇱', PT: '🇵🇹',
  QA: '🇶🇦', RO: '🇷🇴', RU: '🇷🇺', RW: '🇷🇼', KN: '🇰🇳', LC: '🇱🇨', VC: '🇻🇨', WS: '🇼🇸',
  SM: '🇸🇲', ST: '🇸🇹', SA: '🇸🇦', SN: '🇸🇳', RS: '🇷🇸', SC: '🇸🇨', SL: '🇸🇱', SG: '🇸🇬',
  SK: '🇸🇰', SI: '🇸🇮', SB: '🇸🇧', SO: '🇸🇴', ZA: '🇿🇦', KR: '🇰🇷', SS: '🇸🇸', ES: '🇪🇸',
  LK: '🇱🇰', SD: '🇸🇩', SR: '🇸🇷', SE: '🇸🇪', CH: '🇨🇭', SY: '🇸🇾', TW: '🇹🇼', TJ: '🇹🇯',
  TZ: '🇹🇿', TH: '🇹🇭', TL: '🇹🇱', TG: '🇹🇬', TO: '🇹🇴', TT: '🇹🇹', TN: '🇹🇳', TR: '🇹🇷',
  TM: '🇹🇲', TV: '🇹🇻', UG: '🇺🇬', UA: '🇺🇦', AE: '🇦🇪', GB: '🇬🇧', US: '🇺🇸', UY: '🇺🇾',
  UZ: '🇺🇿', VU: '🇻🇺', VA: '🇻🇦', VE: '🇻🇪', VN: '🇻🇳', YE: '🇾🇪', ZM: '🇿🇲', ZW: '🇿🇼',
}

const COUNTRY_NAMES: Partial<Record<CountryCode, string>> = {
  NG: 'Nigeria', KE: 'Kenya', GH: 'Ghana', ZA: 'South Africa', EG: 'Egypt',
  ET: 'Ethiopia', TZ: 'Tanzania', UG: 'Uganda', RW: 'Rwanda', CM: 'Cameroon',
  US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia',
  DE: 'Germany', FR: 'France', IT: 'Italy', ES: 'Spain', NL: 'Netherlands',
  IN: 'India', CN: 'China', JP: 'Japan', KR: 'South Korea', BR: 'Brazil',
  MX: 'Mexico', AR: 'Argentina', CO: 'Colombia', CL: 'Chile', PE: 'Peru',
  AE: 'UAE', SA: 'Saudi Arabia', IL: 'Israel', TR: 'Turkey', IR: 'Iran',
  PK: 'Pakistan', BD: 'Bangladesh', ID: 'Indonesia', MY: 'Malaysia', TH: 'Thailand',
  VN: 'Vietnam', PH: 'Philippines', SG: 'Singapore', HK: 'Hong Kong', TW: 'Taiwan',
}

const COUNTRY_NAME_OVERRIDES: Partial<Record<CountryCode, string>> = {
  AC: 'Ascension Island',
  TA: 'Tristan da Cunha',
  XK: 'Kosovo',
}

const countryNameFormatter =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null

function getCountryDisplayName(countryCode: CountryCode): string {
  return (
    COUNTRY_NAME_OVERRIDES[countryCode] ||
    COUNTRY_NAMES[countryCode] ||
    countryNameFormatter?.of(countryCode) ||
    countryCode
  )
}

function getCurrencyForCountry(countryCode: CountryCode): { code: string; symbol: string } {
  const CURRENCY_MAP: Partial<Record<CountryCode, { code: string; symbol: string }>> = {
    NG: { code: 'NGN', symbol: '₦' }, KE: { code: 'KES', symbol: 'KSh' }, GH: { code: 'GHS', symbol: 'GH₵' },
    ZA: { code: 'ZAR', symbol: 'R' }, EG: { code: 'EGP', symbol: 'E£' }, ET: { code: 'ETB', symbol: 'Br' },
    TZ: { code: 'TZS', symbol: 'TSh' }, UG: { code: 'UGX', symbol: 'USh' }, RW: { code: 'RWF', symbol: 'FRw' },
    CM: { code: 'XAF', symbol: 'FCFA' }, US: { code: 'USD', symbol: '$' }, CA: { code: 'CAD', symbol: 'C$' },
    GB: { code: 'GBP', symbol: '£' }, AU: { code: 'AUD', symbol: 'A$' },
    DE: { code: 'EUR', symbol: '€' }, FR: { code: 'EUR', symbol: '€' }, IT: { code: 'EUR', symbol: '€' },
    ES: { code: 'EUR', symbol: '€' }, NL: { code: 'EUR', symbol: '€' }, IN: { code: 'INR', symbol: '₹' },
    CN: { code: 'CNY', symbol: '¥' }, JP: { code: 'JPY', symbol: '¥' }, KR: { code: 'KRW', symbol: '₩' },
    BR: { code: 'BRL', symbol: 'R$' }, MX: { code: 'MXN', symbol: 'Mex$' }, AE: { code: 'AED', symbol: 'د.إ' },
    SA: { code: 'SAR', symbol: '﷼' }, PK: { code: 'PKR', symbol: '₨' }, BD: { code: 'BDT', symbol: '৳' },
    ID: { code: 'IDR', symbol: 'Rp' }, MY: { code: 'MYR', symbol: 'RM' }, TH: { code: 'THB', symbol: '฿' },
    VN: { code: 'VND', symbol: '₫' }, PH: { code: 'PHP', symbol: '₱' }, SG: { code: 'SGD', symbol: 'S$' },
  }
  return CURRENCY_MAP[countryCode] || { code: 'USD', symbol: '$' }
}

export function getAllCountries(): CountryInfo[] {
  const countries = getCountries()
  return countries.map((code) => {
    const currency = getCurrencyForCountry(code)
    return {
      code,
      name: getCountryDisplayName(code),
      dialCode: `+${getCountryCallingCode(code)}`,
      flag: FLAG_EMOJIS[code] || '🏳️',
      currency: currency.code,
      currencySymbol: currency.symbol,
    }
  }).sort((a, b) => a.name.localeCompare(b.name))
}

export function getCountryInfo(countryCode: CountryCode): CountryInfo | null {
  try {
    const currency = getCurrencyForCountry(countryCode)
    return {
      code: countryCode,
      name: getCountryDisplayName(countryCode),
      dialCode: `+${getCountryCallingCode(countryCode)}`,
      flag: FLAG_EMOJIS[countryCode] || '🏳️',
      currency: currency.code,
      currencySymbol: currency.symbol,
    }
  } catch {
    return null
  }
}

export function getDialCode(countryCode: CountryCode): string {
  try {
    return `+${getCountryCallingCode(countryCode)}`
  } catch {
    return '+1'
  }
}

export function parsePhone(phone: string, defaultCountry?: CountryCode): PhoneValidationResult {
  try {
    const parsed = parsePhoneNumber(phone, defaultCountry)
    if (!parsed) {
      return { isValid: false }
    }
    const typeMap: Record<string, PhoneValidationResult['type']> = {
      MOBILE: 'mobile',
      'FIXED_LINE': 'fixed-line',
      'TOLL_FREE': 'toll-free',
      'PREMIUM_RATE': 'premium-rate',
    }
    return {
      isValid: parsed.isValid(),
      country: parsed.country,
      nationalNumber: parsed.nationalNumber,
      internationalNumber: parsed.formatInternational(),
      type: typeMap[parsed.getType() || ''] || 'unknown',
    }
  } catch {
    return { isValid: false }
  }
}

export function validatePhone(phone: string, countryCode?: CountryCode): boolean {
  if (countryCode) {
    return isValidPhoneNumber(phone, countryCode)
  }
  return isValidPhoneNumber(phone)
}

export function formatPhoneInternational(phone: string, defaultCountry?: CountryCode): string {
  try {
    const parsed = parsePhoneNumber(phone, defaultCountry)
    return parsed?.formatInternational() || phone
  } catch {
    return phone
  }
}

export function formatPhoneNational(phone: string, defaultCountry?: CountryCode): string {
  try {
    const parsed = parsePhoneNumber(phone, defaultCountry)
    return parsed?.formatNational() || phone
  } catch {
    return phone
  }
}

export function getCountryFromPhone(phone: string): CountryCode | undefined {
  try {
    const parsed = parsePhoneNumber(phone)
    return parsed?.country
  } catch {
    return undefined
  }
}

export function getCurrencyInfo(currencyCode: string) {
  return currencyCodes.code(currencyCode)
}

export function getCurrencyForCountryCode(countryCode: CountryCode) {
  return getCurrencyForCountry(countryCode)
}

export const DEFAULT_COUNTRY: CountryCode = 'NG'

export const COMMON_COUNTRIES: CountryCode[] = [
  'NG', 'KE', 'GH', 'ZA', 'EG', 'US', 'GB', 'CA', 'AU', 'IN', 'CN', 'JP',
]
